<template>
    <div class="p-3 d-flex flex-column" style="overflow: hidden">

        <assets-filter-component />

        <div
            class="scroll flex-grow-1"
            ref="personsList"
        >
            <div
                class="task person-list-item"
                :class="{ 'selectable': selectMode }"
                v-for="filteredPerson in filteredPersons.data"
                :key="filteredPerson.id"
                :draggable="true"
                @dragstart="dragStart($event, filteredPerson.id)"
                @click="selectMode && onSelect(filteredPerson)"
            >
                <div
                    class="py-2 task__title"
                    :style="personStyle(filteredPerson)"
                >
                    <p
                        class="m-0 font-weight-bold d-flex align-items-start"
                        style="line-height: 18px"
                    >
                        <span
                            v-if="filteredPerson.latest_badge_time !== undefined"
                            class="status-dot mr-1 mt-1"
                            :class="
                                filteredPerson.latest_badge_time?.type === 'in' ? 'green' : 'red'
                            "
                        ></span>
                        <span v-if="filteredPerson.first_name">
                            {{ filteredPerson.first_name + ' ' }}
                        </span>
                        <span>{{ filteredPerson.last_name }}</span>
                        <span v-if="selectMode && isSelected(filteredPerson.id)" class="ml-auto">
                            <i class="icofont-check selected-check-icon"></i>
                        </span>
                    </p>
                </div>
                <div v-if="filteredPerson.remaining_time_formatted" class="text-center">
                    <div class="custom-progress">
                        <div 
                            class="custom-progress-bar"
                            :style="{ width: `${(filteredPerson.slot_time / filteredPerson.working_time * 100) || 0}%` }"
                        ></div>
                        <span class="custom-progress-text">
                            {{ filteredPerson.remaining_time_formatted }} / {{ filteredPerson.working_time_formatted }}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
var qs = require('qs');
import {emitter} from "../../../plugins/Mitt";
import AssetsFilterComponent from "./filter/AssetsFilterComponent";
import {mapActions, mapState} from "vuex";
import { buildPlanningFilter } from "../../../utils/planning/buildPlanningFilter";

export default {
    components: {
        AssetsFilterComponent,
    },
    props: {
        selectMode: {
            type: Boolean,
            default: false,
        },
        selectedIds: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            canLoadMore: true,
            filteredPersons: {
                total: 0,
                per_page: 100,
                from: 1,
                to: 0,
                current_page: 1,
                data: [],
            },
            loading: false,
            scrollListenerAttached: false,
        };
    },
    computed:{
        ...mapState('planningFilter', [
            'filter',
            'sort',
        ]),
    },
    methods: {
        getFilteredPersons() {
            if ( (this.loading && this.filteredPersons.current_page === 1) || !this.canLoadMore ) return;
            this.loading = true;
            const url = route('persons.filtered-persons');

            const allowedFilters = ['name', 'free', 'department_ids', 'team_ids', 'job_ids', 'certificate_ids', 'skills'];
            const dateOptions = this.filter.free ? { date: this.filter.free } : {};
            const filter = buildPlanningFilter(this.filter, allowedFilters, dateOptions);

            const params = {
                page: this.filteredPersons.current_page,
                filter: filter,
                sort: this.sort,
            };
            axios
                .get(url, {
                    params,
                    paramsSerializer: (params) => {
                        // Ensure empty arrays are represented as empty strings to be included in the query
                        if (params.filter?.department_ids?.id && Array.isArray(params.filter.department_ids.id) && params.filter.department_ids.id.length === 0) {
                            params.filter.department_ids.id = [''];
                        }
                        if (params.filter?.team_ids?.id && Array.isArray(params.filter.team_ids.id) && params.filter.team_ids.id.length === 0) {
                            params.filter.team_ids.id = [''];
                        }
                        return qs.stringify(params, {
                            encode: false,
                            arrayFormat: 'indices'
                        });
                    },
                })
                .then((response) => {
                    const newPersonsData = response?.data?.filteredPersons;
                    if (!newPersonsData || !Array.isArray(newPersonsData.data)) {
                        this.canLoadMore = false; // Stop loading if structure is wrong
                        if (this.filteredPersons.current_page === 1) this.filteredPersons.data = [];
                        return;
                    }

                    const newItemsCount = newPersonsData.data.length;

                    if (newPersonsData.current_page > 1) {
                        this.filteredPersons.data = this.filteredPersons.data.concat(
                            newPersonsData.data,
                        );
                    } else {
                        // Replace data, but merge other pagination properties
                        this.filteredPersons = newPersonsData;
                    }

                    this.filteredPersons.current_page = newPersonsData.current_page;

                    this.canLoadMore = newItemsCount === this.filteredPersons.per_page && newItemsCount > 0;
                })
                .catch(error => { // *** ADD/ENSURE CATCH BLOCK ***
                    this.canLoadMore = false; // Stop loading on error
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        handleScroll() {
            const listElm = this.$refs.personsList;

            if (!listElm || this.loading || !this.canLoadMore) return;

            const currentScroll = listElm.scrollTop;
            const clientHeight = listElm.clientHeight;
            const scrollHeight = listElm.scrollHeight;
            const buffer = 100; // Your buffer
            const triggerPoint = scrollHeight - buffer;
            const currentPosition = currentScroll + clientHeight;

            if (currentPosition >= triggerPoint) {
                // Log when the condition is met *before* fetching

                this.filteredPersons.current_page += 1;
                this.getFilteredPersons();
            }
        },
        personsListControlPanelScroller() {
            this.$nextTick(() => {
                const listElm = this.$refs.personsList;

                if (listElm && !this.scrollListenerAttached) {
                    listElm.addEventListener('scroll', this.handleScroll);
                    this.scrollListenerAttached = true;
                } else if (listElm && this.scrollListenerAttached) {
                }
            });
        },
        removeScrollListener() {
            const listElm = this.$refs.personsList;
            if (listElm && this.scrollListenerAttached) {
                listElm.removeEventListener('scroll', this.handleScroll);
                this.scrollListenerAttached = false;
            }
        },

        dragStart(event, personId) {
            //remove draginfo
            this.$store.commit('planning/resetDragStartInfo');
            event.dataTransfer.setData('personId', personId);
        },
        onSelect(person) {
            const name = [person.first_name, person.last_name].filter(Boolean).join(' ').trim();
            this.$emit('selected', { type: 'person', id: person.id, name });
        },
        isSelected(id){
            return Array.isArray(this.selectedIds) && this.selectedIds.includes(id);
        },
        personStyle: function (person) {
            return {
                'background-color': person.status.color,
                'color': person.status.text_color,
            }
        }
    },
    mounted() {
        this.personsListControlPanelScroller();

        if (this.selectMode) {
            // Ensure empty search when used in picker select mode
            try {
                const planningFilterState = this.$store.state.planningFilter;
                this.filteredPersons.current_page = 1;
                this.canLoadMore = true;
                this.$store.dispatch('planningFilter/storeFilter', {
                    filter: { ...planningFilterState.filter, name: '' },
                    sort: planningFilterState.sort,
                    saveFilter: false,
                });
            } catch (e) {}
        } else {
            this.getFilteredPersons();
        }

        emitter.on('planning-slots-changed', () => {
            console.log('planning-slots-changed');
            this.filteredPersons.current_page = 1;
            this.canLoadMore = true;
            this.getFilteredPersons();
        });
        emitter.on('planning-drop-handled', () => {
            console.log('planning-drop-handled');
            this.filteredPersons.current_page = 1;
            this.canLoadMore = true;
            this.getFilteredPersons();
        });
        emitter.on('slot-deleted', () => {
            console.log('slot-deleted');
            this.filteredPersons.current_page = 1;
            this.canLoadMore = true;
            this.getFilteredPersons();
        });
    },
    beforeUnmount() {
        this.removeScrollListener();
    },
    watch: {
        filter: {
            handler() {
                this.canLoadMore = true;
                this.filteredPersons.current_page = 1;
                this.filteredPersons.data = [];

                this.getFilteredPersons();
            },
            deep: true,
        },
        sort: {
            handler() {
                this.canLoadMore = true;
                this.filteredPersons.current_page = 1;
                this.filteredPersons.data = [];

                this.getFilteredPersons();
            },
            deep: true,
        },
    }
};
</script>

<style>
.scroll {
    overflow-y: auto;
    overflow-x: hidden;
}

.person-list-item{
    display: block;
}

.person-list-item.selectable{
    cursor: pointer;
}

.selected-check-icon{
    color: #fff;
    font-size: 1.2em;
}

.custom-progress {
    background-color: #999;
    height: 15px;
    position: relative;
    overflow: hidden;
}

.custom-progress-bar {
    background-color: #28a745;
    height: 100%;
    transition: width 0.3s ease;
    position: absolute;
    top: 0;
    left: 0;
}

.custom-progress-text {
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    font-size: 0.75rem;
    font-weight: 600;
    white-space: nowrap;
    z-index: 1;
}
</style>
